import Cookies from 'js-cookie';

const AUTH_COOKIE = 'authToken';
const USER_NAME_COOKIE = 'userName';
const COOKIE_PATH = { path: '/' };

/**
 * Cookies que podem ter ficado em versões anteriores:
 *  - `user`     : armazenava o objeto inteiro do usuário (incluindo password/email/permissions);
 *                 não é mais usado e precisa ser purgado por segurança.
 *  - `userUUID` : identificador legado, substituído pelo JWT.
 *  - `authToken`/`userName` : também limpos em rotações de path para evitar resíduos em paths
 *    diferentes (o navegador trata o mesmo nome em paths distintos como cookies separados).
 */
const LEGACY_COOKIE_NAMES = [AUTH_COOKIE, USER_NAME_COOKIE, 'user', 'userUUID'];
const LEGACY_COOKIE_PATHS = ['/', '/login', '/admin', '/app'];

function purgeAuthCookies() {
    const paths = new Set(LEGACY_COOKIE_PATHS);
    if (typeof window !== 'undefined' && window.location && window.location.pathname) {
        paths.add(window.location.pathname);
    }
    LEGACY_COOKIE_NAMES.forEach((name) => {
        Cookies.remove(name);
        paths.forEach((path) => {
            Cookies.remove(name, { path });
        });
    });
}

function decodePayload(token) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const json = decodeURIComponent(
            atob(padded)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(json);
    } catch (e) {
        return null;
    }
}

/**
 * Grava a sessão autenticada. Do objeto `user` é persistido apenas o nome de exibição —
 * dados sensíveis (password, email, permissões, companyId, etc.) ficam exclusivamente
 * no servidor e são consultados sob demanda via endpoints autenticados.
 */
export function setAuthSession(token, user, expiresInMillis) {
    // Garante que não vai existir nenhuma cópia residual do cookie em outro path antes de gravar
    // o novo valor — senão `Cookies.get` pode devolver o cookie antigo (inválido) e o backend
    // responde 401 com a Bearer errada.
    purgeAuthCookies();
    const expires = expiresInMillis
        ? new Date(Date.now() + expiresInMillis)
        : new Date(Date.now() + 24 * 60 * 60 * 1000);
    Cookies.set(AUTH_COOKIE, token, { expires, ...COOKIE_PATH });
    const userName = user && typeof user.name === 'string' ? user.name : '';
    if (userName) {
        Cookies.set(USER_NAME_COOKIE, userName, { expires, ...COOKIE_PATH });
    }
}

export function clearAuthSession() {
    purgeAuthCookies();
}

export function getAuthToken() {
    const token = Cookies.get(AUTH_COOKIE);
    if (!token) return null;
    // Qualquer coisa que não pareça um JWT (3 segmentos) é resíduo de versão anterior —
    // limpa a sessão para não enviar um Bearer inválido em cada request.
    if (token.split('.').length !== 3) {
        clearAuthSession();
        return null;
    }
    const payload = decodePayload(token);
    if (payload && typeof payload.exp === 'number' && payload.exp * 1000 < Date.now()) {
        clearAuthSession();
        return null;
    }
    return token;
}

export function isAuthenticated() {
    return !!getAuthToken();
}

export function getAuthHeader() {
    const token = getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Nome de exibição do usuário autenticado, recuperado do cookie `userName`.
 * Devolve string vazia se não houver sessão ativa.
 */
export function getCurrentUserName() {
    return Cookies.get(USER_NAME_COOKIE) || '';
}

/**
 * ID numérico do usuário autenticado, extraído do JWT (quando presente no payload).
 */
export function getCurrentUserId() {
    const token = getAuthToken();
    if (!token) {
        return null;
    }
    const payload = decodePayload(token);
    if (!payload) {
        return null;
    }
    const raw = payload.userId ?? payload.user_id ?? payload.id ?? payload.sub;
    if (raw == null || raw === '') {
        return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
}
